import axios from 'axios';

// Telemed self-diagnostics (a light "webrtc-internals"). Collects, per session
// and on BOTH sides (doctor and patient), on success AND failure: browser env,
// getUserMedia result, the ICE/signaling state timeline, gathered candidate
// types, ICE errors (onicecandidateerror), the selected candidate pair, and
// periodic getStats samples. Flushes to RTDB (via the recordTelemedDiagnostics
// cloud function) so we can find the cause of failures WITHOUT relying on the
// user's chrome://webrtc-internals (which Safari doesn't even have).

const ROOT_URL = process.env.REACT_APP_CLOUD_FUNCTIONS_ROOT_URL;
const STATS_INTERVAL_MS = 5000;
const MAX_SAMPLES = 12;
const MAX_TIMELINE = 40;
const MAX_ICE_ERRORS = 20;
const SILENT_AUDIO_LEVEL = 0.001;

let record = null;
let attachedPc = null;
let statsTimer = null;
let detachHandlers = null;
let lastGum = null;
let lastIceConfigSource = null;

export function detectEnv() {
  const nav = typeof navigator !== 'undefined' ? navigator : {};
  const ua = nav.userAgent || '';
  const isIOS = /iP(hone|ad|od)/.test(ua)
    || (nav.platform === 'MacIntel' && nav.maxTouchPoints > 1);
  const isSafari = /safari/i.test(ua)
    && !/chrome|crios|chromium|android|edg|fxios|firefox/i.test(ua);
  let browser = 'other';
  if (/edg/i.test(ua)) browser = 'edge';
  else if (/chrome|crios|chromium/i.test(ua)) browser = 'chrome';
  else if (/firefox|fxios/i.test(ua)) browser = 'firefox';
  else if (isSafari) browser = 'safari';
  const versionMatch = /(version|chrome|crios|firefox|fxios|edg)\/(\d+)/i.exec(ua);
  const version = versionMatch ? versionMatch[2] : null;
  return {
    userAgent: ua,
    platform: nav.platform || null,
    browser,
    version,
    isSafari,
    isIOS,
  };
}

// error.name is the stable discriminator; we also capture what we can about the
// granted stream (missing/muted track is a real cause of "mic doesn't work").
export function recordGum(info) {
  lastGum = info || null;
  if (record) {
    record.gum = lastGum;
  }
}

// Which ICE config the call used: 'turn' (Twilio credentials) or 'stun-fallback'
// (Google STUN only, when getTelemedIceServers failed client-side). Resolves the
// relay:0 ambiguity — fallback vs the network blocking TURN.
export function recordIceConfigSource(source) {
  lastIceConfigSource = source || null;
  if (record) {
    record.iceConfigSource = lastIceConfigSource;
  }
}

// Did the doctor see the patient waiting for THIS room? Splits no-show from
// showed-up-but-failed. Doctor record only — self-gates on role/room.
export function recordPatientPresenceSeen(roomId) {
  if (record && record.role === 'professional' && record.roomId === roomId) {
    record.patientPresenceSeen = true;
  }
}

function isAudioReport(report) {
  return report.kind === 'audio' || report.mediaType === 'audio';
}

function parseCandidate(candidate) {
  const str = (candidate && candidate.candidate) || '';
  const typeMatch = /typ (\w+)/.exec(str);
  const type = (candidate && candidate.type) || (typeMatch ? typeMatch[1] : null);
  return { type };
}

function findSelectedPair(stats) {
  let pair = null;
  stats.forEach((report) => {
    if (report.type === 'transport' && report.selectedCandidatePairId) {
      pair = stats.get(report.selectedCandidatePairId) || pair;
    }
  });
  if (!pair) {
    stats.forEach((report) => {
      if (report.type === 'candidate-pair' && report.nominated && report.state === 'succeeded') {
        pair = report;
      }
    });
  }
  return pair;
}

// Snapshot of the high-value getStats fields. Reused by the TURN telemetry
// (didConnectionUseTurn in the saga) and the diagnostics sampler. Null-check
// every field: Safari omits many and they can vanish between reports.
export async function collectStatsSnapshot(pc) {
  if (!pc || !pc.getStats) return null;
  try {
    const stats = await pc.getStats();
    const snapshot = {
      t: Date.now(),
      usedTurn: false,
      selectedPair: null,
      outbound: {},
      inbound: {},
    };
    const pair = findSelectedPair(stats);
    if (pair) {
      const local = stats.get(pair.localCandidateId);
      const remote = stats.get(pair.remoteCandidateId);
      const localType = local ? local.candidateType : null;
      const remoteType = remote ? remote.candidateType : null;
      snapshot.usedTurn = localType === 'relay' || remoteType === 'relay';
      snapshot.selectedPair = {
        localType: localType || null,
        remoteType: remoteType || null,
        protocol: (local && local.protocol) || null,
        relayProtocol: (local && local.relayProtocol) || null,
        rtt: pair.currentRoundTripTime != null ? pair.currentRoundTripTime : null,
        nominated: pair.nominated != null ? pair.nominated : null,
        state: pair.state || null,
      };
    }
    stats.forEach((report) => {
      if (report.type === 'media-source' && isAudioReport(report)) {
        if (report.audioLevel != null) snapshot.outbound.audioLevel = report.audioLevel;
        if (report.totalAudioEnergy != null) snapshot.outbound.totalAudioEnergy = report.totalAudioEnergy;
      }
      if (report.type === 'outbound-rtp' && isAudioReport(report)) {
        if (report.packetsSent != null) snapshot.outbound.packetsSent = report.packetsSent;
        if (report.bytesSent != null) snapshot.outbound.bytesSent = report.bytesSent;
      }
      if (report.type === 'inbound-rtp' && isAudioReport(report)) {
        if (report.packetsReceived != null) snapshot.inbound.packetsReceived = report.packetsReceived;
        if (report.bytesReceived != null) snapshot.inbound.bytesReceived = report.bytesReceived;
        if (report.packetsLost != null) snapshot.inbound.packetsLost = report.packetsLost;
        if (report.jitter != null) snapshot.inbound.jitter = report.jitter;
        if (report.audioLevel != null) snapshot.inbound.audioLevel = report.audioLevel;
      }
    });
    return snapshot;
  } catch (error) {
    console.warn('collectStatsSnapshot failed', error);
    return null;
  }
}

function pushTimeline() {
  if (!record || !attachedPc) return;
  const entry = {
    t: Date.now(),
    connectionState: attachedPc.connectionState || null,
    iceConnectionState: attachedPc.iceConnectionState || null,
    iceGatheringState: attachedPc.iceGatheringState || null,
    signalingState: attachedPc.signalingState || null,
  };
  const last = record.iceTimeline[record.iceTimeline.length - 1];
  if (last
    && last.connectionState === entry.connectionState
    && last.iceConnectionState === entry.iceConnectionState
    && last.iceGatheringState === entry.iceGatheringState
    && last.signalingState === entry.signalingState) {
    return;
  }
  record.iceTimeline.push(entry);
  if (record.iceTimeline.length > MAX_TIMELINE) record.iceTimeline.shift();
}

// onicecandidateerror reports TURN/STUN failures (errorCode 701 = unreachable,
// 401 = auth). Chromium-only — silence on Safari is itself a signal.
function onIceError(event) {
  if (!record) return;
  record.iceErrors.push({
    errorCode: event.errorCode != null ? event.errorCode : null,
    url: event.url || null,
    errorText: event.errorText || null,
  });
  if (record.iceErrors.length > MAX_ICE_ERRORS) record.iceErrors.shift();
}

function onLocalCandidate(event) {
  if (!record || !event.candidate) return;
  const { type } = parseCandidate(event.candidate);
  if (type && record.candidates[type] != null) {
    record.candidates[type] += 1;
  }
  // Relay candidates: bucket the client↔TURN transport (udp/tcp/tls). When
  // usedTurn:false, shows if TCP/443 relay was formed. relayProtocol is
  // best-effort (Chromium exposes it, Safari may not) → 'unknown'.
  if (type === 'relay') {
    const proto = event.candidate.relayProtocol || 'unknown';
    if (record.relayByProtocol[proto] != null) {
      record.relayByProtocol[proto] += 1;
    } else {
      record.relayByProtocol.unknown += 1;
    }
  }
}

async function sampleStats(pc) {
  // PC closed (tab left open after the call, laptop slept): stop sampling and
  // drop the record — the call is over, nothing to record. Avoids the "zombie
  // record" (empty samples flushed later with a bogus class).
  if (!pc || pc.connectionState === 'closed') {
    if (statsTimer) {
      clearInterval(statsTimer);
      statsTimer = null;
    }
    record = null;
    return;
  }
  const snap = await collectStatsSnapshot(pc);
  if (!snap || !record) return;
  if (snap.selectedPair) {
    record.selectedPair = snap.selectedPair;
    if (snap.usedTurn) record.usedTurn = true;
  }
  record.statsSamples.push({
    t: snap.t,
    rtt: snap.selectedPair ? snap.selectedPair.rtt : null,
    outAudioLevel: snap.outbound.audioLevel != null ? snap.outbound.audioLevel : null,
    outPacketsSent: snap.outbound.packetsSent != null ? snap.outbound.packetsSent : null,
    inAudioLevel: snap.inbound.audioLevel != null ? snap.inbound.audioLevel : null,
    inPacketsReceived: snap.inbound.packetsReceived != null ? snap.inbound.packetsReceived : null,
    inPacketsLost: snap.inbound.packetsLost != null ? snap.inbound.packetsLost : null,
    inJitter: snap.inbound.jitter != null ? snap.inbound.jitter : null,
  });
  if (record.statsSamples.length > MAX_SAMPLES) record.statsSamples.shift();
}

// Classifies the session from the samples/timeline fingerprints. Applies on
// success too: outcome 'connected' + failureClass 'silent_mic'/'connected_no_media'
// describes "connected but no audio" — distinguishes today's report from a real
// failure.
function computeFailureClass(rec, outcome) {
  const samples = rec.statsSamples;
  const last = samples[samples.length - 1];
  const first = samples[0];
  const everConnected = outcome === 'connected' || outcome === 'ended'
    || rec.iceTimeline.some((e) => e.connectionState === 'connected');

  if (rec.gum && rec.gum.audioTracks > 0 && samples.length >= 2) {
    const sentGrew = (last && last.outPacketsSent != null ? last.outPacketsSent : 0)
      > (first && first.outPacketsSent != null ? first.outPacketsSent : 0);
    const levels = samples.map((s) => s.outAudioLevel).filter((v) => v != null);
    const maxLevel = levels.length ? Math.max(...levels) : null;
    if (sentGrew && maxLevel != null && maxLevel < SILENT_AUDIO_LEVEL) {
      return 'silent_mic';
    }
  }

  if (everConnected && samples.length >= 2) {
    const recvGrew = (last && last.inPacketsReceived != null ? last.inPacketsReceived : 0)
      > (first && first.inPacketsReceived != null ? first.inPacketsReceived : 0);
    if (!recvGrew && last && (last.inPacketsReceived === 0 || last.inPacketsReceived == null)) {
      return 'connected_no_media';
    }
  }

  if (outcome === 'connected' || outcome === 'ended') return null;
  // Connected then lost ≠ never connected — don't inflate ice_failed with drops.
  if (everConnected) return 'dropped_after_connect';
  if (rec.candidates.relay === 0) return 'no_relay_candidate';
  if (outcome === 'timed_out') return 'timed_out';
  return 'ice_failed';
}

// Timeline-derived flags (no new listeners) so the dominant pattern is queryable
// directly: did the answer come back? did ICE reach checking?
function computeSignalingFlags(tl) {
  const offerIdx = tl.findIndex((e) => (
    e.signalingState === 'have-local-offer' || e.signalingState === 'have-remote-offer'
  ));
  return {
    answerReceived: offerIdx !== -1
      && tl.slice(offerIdx + 1).some((e) => e.signalingState === 'stable'),
    reachedChecking: tl.some((e) => (
      e.iceConnectionState === 'checking'
      || e.iceConnectionState === 'connected'
      || e.iceConnectionState === 'completed'
    )),
  };
}

function recordDiagnosticsRequest(payload) {
  return axios.post(`${ROOT_URL}/recordTelemedDiagnostics`, payload);
}

export function stopDiagnostics() {
  if (statsTimer) {
    clearInterval(statsTimer);
    statsTimer = null;
  }
  if (detachHandlers) {
    detachHandlers();
    detachHandlers = null;
  }
  attachedPc = null;
}

export function startDiagnostics(pc, { role, roomId, professionalId } = {}) {
  // Doesn't flush: each new peerConnection (retry included) starts a fresh
  // record; the last state per room/role wins in RTDB.
  stopDiagnostics();
  if (!pc) return;
  record = {
    role: role || null,
    roomId: roomId || null,
    professionalId: professionalId || null,
    env: detectEnv(),
    gum: lastGum || null,
    iceConfigSource: lastIceConfigSource || null,
    iceTimeline: [],
    candidates: {
      host: 0,
      srflx: 0,
      prflx: 0,
      relay: 0,
    },
    relayByProtocol: {
      udp: 0,
      tcp: 0,
      tls: 0,
      unknown: 0,
    },
    iceErrors: [],
    selectedPair: null,
    statsSamples: [],
    outcome: null,
    failureClass: null,
    usedTurn: false,
    patientPresenceSeen: false,
    createdAt: new Date().toISOString(),
    endedAt: null,
  };
  attachedPc = pc;
  pushTimeline();
  const handlers = {
    connectionstatechange: () => pushTimeline(),
    iceconnectionstatechange: () => pushTimeline(),
    icegatheringstatechange: () => pushTimeline(),
    signalingstatechange: () => pushTimeline(),
    icecandidateerror: (event) => onIceError(event),
    icecandidate: (event) => onLocalCandidate(event),
  };
  Object.keys(handlers).forEach((type) => pc.addEventListener(type, handlers[type]));
  detachHandlers = () => {
    Object.keys(handlers).forEach((type) => {
      try {
        pc.removeEventListener(type, handlers[type]);
      } catch (e) { /* no-op */ }
    });
  };
  statsTimer = setInterval(() => {
    sampleStats(pc);
  }, STATS_INTERVAL_MS);
}

// A real connection attempt happened (the peers started pairing), not just a
// room opened with the other side never joining. Used to skip recording
// "abandoned" from someone who only opened and closed.
function hadConnectionAttempt(rec) {
  return rec.iceTimeline.some((e) => (
    e.connectionState === 'connecting'
    || e.connectionState === 'connected'
    || e.connectionState === 'failed'
    || e.iceConnectionState === 'checking'
    || e.iceConnectionState === 'connected'
    || e.iceConnectionState === 'failed'
  ));
}

// Flushes the record at terminal points (failed/timed_out/ended/abandoned). Only
// persists to RTDB on a PROBLEM: connection failure, abandon after a real
// attempt, or a success with failureClass (e.g. connected but mic muted / no
// media). Clean success doesn't go to RTDB — the OK-call denominator is already
// counted by telemedStats.
export async function flushDiagnostics(outcome) {
  if (!record) return;
  try {
    if (attachedPc) {
      await sampleStats(attachedPc);
    }
    // sampleStats pode ter descartado o registro (PC fechado) — nada a gravar.
    if (!record) return;
    record.outcome = outcome;
    record.endedAt = new Date().toISOString();
    record.failureClass = computeFailureClass(record, outcome);
    const sig = computeSignalingFlags(record.iceTimeline);
    record.answerReceived = sig.answerReceived;
    record.reachedChecking = sig.reachedChecking;
    const isFailure = outcome === 'failed' || outcome === 'timed_out';
    const isAbandonedAttempt = outcome === 'abandoned' && hadConnectionAttempt(record);
    const shouldPersist = isFailure || isAbandonedAttempt || !!record.failureClass;
    if (shouldPersist && record.professionalId && record.roomId) {
      // JSON round-trip remove `undefined` (o RTDB rejeita undefined).
      const payload = {
        role: record.role,
        roomId: record.roomId,
        professionalId: record.professionalId,
        record: JSON.parse(JSON.stringify(record)),
      };
      await recordDiagnosticsRequest(payload);
    }
  } catch (error) {
    console.warn('flushDiagnostics failed', error);
  } finally {
    stopDiagnostics();
    record = null;
  }
}

// Emergency flush on unload (pagehide): tab close kills the axios flush, so send
// the current record via keepalive (survives unload, like setTelemedPresence).
// No final getStats sample (async, won't run on unload); uses samples already
// collected. Idempotent (backend .set(), last wins). No-op if there's no record.
// Does NOT clear the record: bfcache may bring the page back.
export function flushDiagnosticsBeacon() {
  if (!record || !ROOT_URL) return;
  try {
    const everConnected = record.iceTimeline.some((e) => e.connectionState === 'connected');
    const outcome = everConnected ? 'ended' : 'abandoned';
    record.outcome = outcome;
    record.endedAt = new Date().toISOString();
    record.failureClass = computeFailureClass(record, outcome);
    const sig = computeSignalingFlags(record.iceTimeline);
    record.answerReceived = sig.answerReceived;
    record.reachedChecking = sig.reachedChecking;
    // Same gate as flushDiagnostics for these outcomes: abandon after a real
    // attempt, or a failureClass. Clean 'ended' isn't persisted (telemedStats).
    const isAbandonedAttempt = outcome === 'abandoned' && hadConnectionAttempt(record);
    const shouldPersist = isAbandonedAttempt || !!record.failureClass;
    if (shouldPersist && record.professionalId && record.roomId) {
      const payload = {
        role: record.role,
        roomId: record.roomId,
        professionalId: record.professionalId,
        record: JSON.parse(JSON.stringify(record)),
      };
      fetch(`${ROOT_URL}/recordTelemedDiagnostics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    }
  } catch (error) {
    console.warn('flushDiagnosticsBeacon failed', error);
  }
}
